import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import type { Ref, SetStateAction } from 'react';

import { tokens } from '@opendoor/tokens-studio/dist/bricks-next/tokens/Components';
import type { Color } from '@opendoor/tokens-studio/dist/bricks-next/types/Novo';
import { useCheckbox } from '@tamagui/checkbox-headless';
import type { CheckboxProps, CheckedState } from '@tamagui/checkbox-headless';
import { isWeb, Stack, styled, View as TamaguiView, useProps } from '@tamagui/core';
import type { GestureReponderEvent, WithMediaProps } from '@tamagui/core';
import type { View } from 'react-native';

import { Checkmark } from '../../../__generated__/icons';
import type { AnalyticsProps } from '../../../helpers/analytics';
import { useObservabilitySafe } from '../../../helpers/useObservabilitySafe';
import { isKeyOfObject } from '../../../helpers/utils';
import { Box } from '../../Box';
import { TrackableContainer } from '../../shared/TrackableContainer';
import { Text } from '../../Text';
import { useInputId } from '../shared/hooks';
import type { InputProps, InputState } from '../shared/InputProps';
import { getNextCheckedState, resolveCheckedState } from './shared/checkedState';
import { useCheckBoxRadioState } from './shared/hooks';

const { radioCheckbox: checkboxTokens } = tokens;

type SimpleCheckboxSize = keyof typeof checkboxTokens.typography.simpleCheckbox;

type WithLabel = {
  /**
   * Mandatory Label for accessibility for the checkbox
   */
  label: string;
  /**
   * Must be `never` when `label` is present
   */
  ariaLabel?: never;
};

type WithAriaLabel = {
  /**
   * Mandatory Aria label for the checkbox for a11y.
   */
  ariaLabel: string;
  /**
   * Must be 'never' when ariaLabel is present
   */
  label?: never;
};

export type SimpleCheckboxProps = AnalyticsProps &
  Omit<CheckboxProps, 'onPress'> &
  Pick<InputProps, 'invalid' | 'required' | 'id'> &
  (WithLabel | WithAriaLabel) & {
    /**
     * The size of the checkbox. Defaults to 'medium'
     */
    size?: SimpleCheckboxSize;
    /**
     * The checkbox state
     */
    state?: InputState | 'selected';
    /**
     * Value of the specific checkbox
     */
    value: string;
    /**
     * Disables a single checkbox
     */
    disabled?: boolean;
    /**
     * Provides the parent component with a way to view the checked state of each checkbox
     */
    onPress?: (name: string, checked: CheckedState) => void;
    /**
     * If the checkbox is checked by default
     */
    defaultChecked?: CheckedState;
    /**
     * name value for the checkbox
     */
    name?: string;
    /**
     * The current checked state of the checkbox.
     */
    checked?: CheckedState;
    /**
     * Provides the parent component a way to do something on focus event for a checkbox/radio row
     */
    onFocus?: React.FocusEventHandler<HTMLDivElement>;
    /**
     * Provides the parent component a way to do something on blur event for a checkbox/radio row
     */
    onBlur?: React.FocusEventHandler<HTMLDivElement>;
  } & WithMediaProps<{
    /**
     * The size of the checkbox
     */
    size: SimpleCheckboxSize;
  }>;

/**
 * Checkbox provides a checkbox component, users can check/uncheck the option
 * The checkbox is built with [Tamagui's headless checkbox](https://tamagui.dev/docs/components/checkbox?type=headless)
 *
 * **Meaningful Properties:**
 * - Pass an "onPress" function to get access to whether the checkbox is checked
 * - To pre-check the checkbox pass defaultChecked = true
 * - Pass invalid if you want the error state to show up and disabled to disable the checkbox
 *
 *
 * **Analytics:**
 * - Click Events: Checkbox click events will be tracked as ("checkboxClickEvent"), so `analyticsName` is required
 * - Impression Events: If you want to track impression events as ("checkboxViewEvent"), pass
 * `analyticsName` and `trackImpression` if you want to track the impression event
 *
 * **Accessibility:**
 *
 * Keyboard Interaction:
 * - When the checkbox has focus(can tab to focus), pressing the [space](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/checkbox_role#keyboard_interactions) key changes the state of the checkbox
 *
 * Checkbox:
 * - Has role [checkbox](https://w3c.github.io/aria/#checkbox)
 * - Focus indicator is visible and has sufficient contrast
 * - Value attribute attached
 * - [Aria-checked](https://developer.mozilla.org/en-US/docs/web/Accessibility/ARIA/Attributes/aria-checked) attribute updates based on if its checked or not
 * - Has an accessible label because the visible text content is contained within the element with role `checkbox`
 *
 */
export const SimpleCheckbox = forwardRef<View, SimpleCheckboxProps>(function SimpleCheckbox(
  {
    analyticsName,
    label,
    value,
    defaultChecked = false,
    disabled,
    invalid,
    id,
    onPress,
    name,
    trackImpression,
    checked,
    ariaLabel,
    ...props
  },
  ref: Ref<View>,
) {
  const { size = 'medium' } = useProps(props);

  const { trackJourneyEvent } = useObservabilitySafe();
  const [internalChecked, setInternalChecked] = useState(defaultChecked);
  const internalCheckedRef = useRef<CheckedState>(defaultChecked);

  const setTrackedInternalChecked = useCallback((nextChecked: SetStateAction<CheckedState>) => {
    setInternalChecked((currentChecked) => {
      const resolvedChecked = resolveCheckedState(nextChecked, currentChecked);
      internalCheckedRef.current = resolvedChecked;
      return resolvedChecked;
    });
  }, []);

  useEffect(() => {
    if (checked !== undefined) {
      setTrackedInternalChecked(checked);
    }
  }, [checked, setTrackedInternalChecked]);

  const onCheckedChange = (e: GestureReponderEvent) => {
    e.stopPropagation();
    onPress?.(value, getNextCheckedState(internalCheckedRef.current));
    const data = { targetName: analyticsName, optionName: value };
    trackJourneyEvent('platform', 'checkboxClickEvent', data);
  };

  // https://tamagui.dev/docs/components/checkbox?type=headless
  const { checkboxProps, checkboxRef, bubbleInput } = useCheckbox(
    {
      name,
      value,
      defaultChecked,
      disabled,
      onPress: onCheckedChange,
      ...props,
    },
    [internalChecked, setTrackedInternalChecked],
    ref,
  );

  // Generate unique input id using custom hook
  const inputId = useInputId(id);

  // Custom hook for managing checkbox and radio button states
  const { rowState, buttonState, handleFocus, handleBlur } = useCheckBoxRadioState({
    checked: internalChecked,
    disabled,
    invalid,
  });

  // Determine checkmark size and color based on checkbox size and state
  const { checkMarkSize, checkMarkColor } = getCheckMarkIconStyle(size, buttonState);

  return (
    <TrackableContainer
      analyticsName={analyticsName}
      trackImpression={trackImpression}
      eventName="checkboxViewEvent"
    >
      <CheckboxRowButton
        {...checkboxProps}
        aria-invalid={isWeb ? invalid : undefined}
        aria-required={isWeb ? props.required : undefined}
        aria-label={ariaLabel}
        ref={checkboxRef}
        size={size}
        state={rowState}
        onFocus={handleFocus}
        onBlur={handleBlur}
        focusable={!disabled}
        id={inputId}
      >
        <Checkbox
          disabled={disabled}
          size={size}
          state={{ state: buttonState, checked: internalChecked }}
          onFocus={handleFocus}
          onBlur={handleBlur}
          // we only want to focus on the row button and not on the checkmark frame
          focusable={false}
          alignSelf="flex-start"
        >
          {internalChecked === true && <Checkmark size={checkMarkSize} color={checkMarkColor} />}
          {bubbleInput}
        </Checkbox>
        {label && (
          <Box alignSelf="center" flex={1}>
            <Text
              alignSelf="flex-start"
              textAlign="left"
              tag="label"
              htmlFor={id}
              cursor="inherit"
              typography={checkboxTokens.typography.simpleCheckbox[size]}
              color={
                isKeyOfObject(buttonState, checkboxTokens.color.content.valueText.state)
                  ? checkboxTokens.color.content.valueText.state[buttonState]
                  : checkboxTokens.color.content.valueText.default
              }
            >
              {label}
            </Text>
          </Box>
        )}
      </CheckboxRowButton>
    </TrackableContainer>
  );
});

/**
 * Components used in Checkbox:
 *
 * - Checkbox Row component:
 * This is treated as a button so we can:
 * 1) Add the right accessibility roles when this container is selected
 * 2) Enable onPress/onSpace to work on the container (checks/unchecks the checkbox)
 *
 * - CheckMarkFrame - styling for the box that holds the checkmark
 * - getCheckMarkIconStyle - gets the style for the checkmark
 */
const CheckboxRowButton = styled(TamaguiView, {
  tag: 'button',
  display: 'flex',
  flexDirection: 'row',
  /**
   * Combined size and state into one variant because there are multiple times we need to use both to determine the style (especially for small/xsmall)
   * Seperating these two variants causes confusion and causes strange overrides
   */
  variants: {
    state: (state: SimpleCheckboxProps['state']) => {
      const cursor = state === 'inactive' ? 'default' : 'pointer';
      return {
        cursor,
      };
    },
    size: (size: SimpleCheckboxSize) => {
      const updatedSize = size === 'xsmall' ? 'small' : size;
      return {
        borderColor: '$transparent',
        backgroundColor: '$transparent',
        paddingTop: checkboxTokens.margin.simpleCheckbox.topBottom[updatedSize],
        paddingBottom: checkboxTokens.margin.simpleCheckbox.topBottom[updatedSize],
        gap: checkboxTokens.padding.simpleCheckbox[updatedSize],
      };
    },
  } as const,
});

// Styled stack component for Checbox
const Checkbox = styled(Stack, {
  borderWidth: checkboxTokens.borderWidth.default,
  alignItems: 'center',
  justifyContent: 'center',
  marginLeft: 'auto',
  alignSelf: 'center',
  variants: {
    size: (size: SimpleCheckboxSize) => {
      const widthAndHeight = {
        xsmall: checkboxTokens.size.element.radio.exterior.xsmall,
        small: checkboxTokens.size.element.radio.exterior.small,
        medium: checkboxTokens.size.element.radio.exterior.medium,
      };
      return {
        borderRadius:
          checkboxTokens.cornerRadius.element.checkbox[
            size === 'small' || size === 'xsmall' ? 'smallXsmall' : size
          ],
        width: widthAndHeight[size],
        height: widthAndHeight[size],
      };
    },
    // eslint-disable-next-line jsdoc/require-jsdoc
    state: ({
      state,
      checked,
    }: {
      // eslint-disable-next-line jsdoc/require-jsdoc
      state: SimpleCheckboxProps['state'];
      // eslint-disable-next-line jsdoc/require-jsdoc
      checked: CheckedState;
    }) => ({
      borderColor: isKeyOfObject(state, checkboxTokens.color.border.element.checkbox.state)
        ? checkboxTokens.color.border.element.checkbox.state[state]
        : checkboxTokens.color.border.element.checkbox.default,
      backgroundColor:
        (checked || state === 'inactive') &&
        isKeyOfObject(state, checkboxTokens.color.background.element.checkboxRadio.state)
          ? checkboxTokens.color.background.element.checkboxRadio.state[state]
          : '$transparent',
    }),
  } as const,
});

/**
 * Helper function to determine checkmark size and color based on checkbox size and state.
 */
const getCheckMarkIconStyle = (
  size: SimpleCheckboxProps['size'],
  state: SimpleCheckboxProps['state'],
): {
  /**
   * The size of the checkmark
   */
  checkMarkSize: 20 | 24;
  /**
   * The color of the checkmark
   */
  checkMarkColor: Color;
} => ({
  checkMarkSize: size === 'small' ? 20 : 24,
  checkMarkColor: isKeyOfObject(state, checkboxTokens.color.content.element.checkIcon.state)
    ? checkboxTokens.color.content.element.checkIcon.state[state]
    : checkboxTokens.color.content.element.checkIcon.default,
});
